import { useState } from 'react';
import { X, ShoppingCart, CheckCircle, Loader2 } from 'lucide-react';
import { useWorkflowStore } from '../admin/store/workflowStore';

interface BuyNowModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: {
    id: string;
    name: string;
    price: number;
    type: 'product' | 'package';
    image?: string;
    source?: string;
  };
}

export default function BuyNowModal({ isOpen, onClose, item }: BuyNowModalProps) {
  const { addOrder } = useWorkflowStore();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [errors, setErrors] = useState<{ name?: string; phone?: string }>({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [orderRef, setOrderRef] = useState('');

  if (!isOpen) return null;

  const validate = () => {
    const e: { name?: string; phone?: string } = {};
    if (!name.trim()) e.name = 'Customer name is required';
    if (!phone.trim()) e.phone = 'Phone number is required';
    else if (!/^[\d\s\+\-\(\)]{7,15}$/.test(phone.trim())) e.phone = 'Enter a valid phone number';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);

    await new Promise((r) => setTimeout(r, 600));

    const ref = addOrder({
      customerName: name.trim(),
      customerPhone: phone.trim(),
      itemType: item.type,
      itemId: item.id,
      itemName: item.name,
      itemImage: item.image,
      itemPrice: item.price,
      quantity: 1,
      orderSource: item.source || 'website',
      status: 'new',
    });

    setOrderRef(ref);
    setLoading(false);
    setSuccess(true);
  };

  const handleClose = () => {
    setName('');
    setPhone('');
    setErrors({});
    setLoading(false);
    setSuccess(false);
    setOrderRef('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md p-8 z-10">
        {/* Close */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
        >
          <X className="w-4 h-4 text-gray-600" />
        </button>

        {success ? (
          // Success state
          <div className="text-center py-4">
            <div className="w-16 h-16 bg-[#F1F6EE] rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-[#7FA86A]" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Order Received!</h3>
            <p className="text-sm text-gray-500 mb-1">Our team will contact you shortly.</p>
            {orderRef && (
              <p className="text-xs text-gray-400 bg-gray-50 rounded-xl px-4 py-2 mt-3 font-mono">
                Order ID: {orderRef}
              </p>
            )}
            <button
              onClick={handleClose}
              className="mt-6 w-full h-12 rounded-2xl bg-[#7FA86A] text-white font-medium hover:bg-[#658C53] transition-colors"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-[#F1F6EE] rounded-xl flex items-center justify-center flex-shrink-0">
                <ShoppingCart className="w-5 h-5 text-[#7FA86A]" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Buy Now</h3>
                <p className="text-xs text-gray-400 line-clamp-1">{item.name}</p>
              </div>
            </div>

            {/* Item summary */}
            <div className="bg-[#F1F6EE] rounded-2xl p-4 mb-6">
              <p className="text-[13px] font-medium text-gray-700 line-clamp-2">{item.name}</p>
              {item.price > 0 && (
                <p className="text-sm font-semibold text-[#7FA86A] mt-1">
                  Rs. {item.price.toLocaleString('en-LK')}
                </p>
              )}
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1.5">
                  Customer Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => { setName(e.target.value); setErrors(v => ({ ...v, name: undefined })); }}
                  placeholder="Your full name"
                  className={`w-full h-11 px-4 rounded-xl border text-[14px] outline-none transition-colors ${errors.name ? 'border-red-400 bg-red-50' : 'border-gray-200 focus:border-[#7FA86A] focus:bg-[#F1F6EE]'}`}
                />
                {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
              </div>

              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1.5">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => { setPhone(e.target.value); setErrors(v => ({ ...v, phone: undefined })); }}
                  placeholder="07X XXX XXXX"
                  className={`w-full h-11 px-4 rounded-xl border text-[14px] outline-none transition-colors ${errors.phone ? 'border-red-400 bg-red-50' : 'border-gray-200 focus:border-[#7FA86A] focus:bg-[#F1F6EE]'}`}
                />
                {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone}</p>}
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full h-12 rounded-2xl bg-[#7FA86A] text-white font-semibold hover:bg-[#658C53] disabled:opacity-60 transition-colors flex items-center justify-center gap-2"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingCart className="w-4 h-4" />}
                {loading ? 'Placing Order...' : 'Place Order'}
              </button>
              <p className="text-[11px] text-gray-400 text-center">
                We'll contact you to confirm your order details.
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
